import { Box, Button } from '@mui/material'
import React from 'react'
import { MonthView } from '../MonthView'
import { TreatmentAppointmentItem } from '../Appointment/TreatmentAppointmentItem'
import { TreatmentSurveyItem } from '../Survey/TreatmentSurveyItem'
import {
  TreatmentItems,
  GroupedTreatmentItem,
  TreatmentItem,
} from '../../utils/treatmentItem.util'
import { ArrowProps } from './Arrow'
import { Appointment, Referral, Survey } from '../../types/treatments.types'
import { TREATMENT_CALENDAR_WIDTH } from '../../../../../config'
import {
  TreatmentAppointmentItemComponentViewModel,
  TreatmentSurveyItemComponentViewModel,
} from '../../types/treatmentItem.types'
import { TreatmentReferralItem } from '../Referral/TreatmentReferralItem'
import { useTreatmentPanelData } from './TreatmentPanelProvider'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { ArrowUpIcon } from '../../../../../common/icons/ArrowUpIcon'

export interface TreatmentGroupedAppointmentsComponentViewModel {
  treatmentItemViewModel: Omit<
    TreatmentAppointmentItemComponentViewModel,
    'appointment' | 'disableLinking' | 'canLinkTo' | 'onClick'
  >
  isNoFilteredResultContent: boolean
  groupedTreatmentItems: GroupedTreatmentItem
  visibleAppointments: Appointment[]
  visibleSurveys: Survey[]
  visibleReferrals: Referral[]
  widthOfArrows: number
  arrowProps: ArrowProps[]
  linkingDisabledIds: string[]
  onClickAppointment: (appointmentId: string) => void
  onClickSurvey: (survey: Survey) => void
  editSurvey: (survey: Survey) => void
  nextTreatmentItem?: TreatmentItem
}

interface Props {
  vm: TreatmentGroupedAppointmentsComponentViewModel
}

export const TreatmentGroupedAppointmentsComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ vm }) => {
  const { showPastTreatmentItems, setShowPastTreatmentItems } =
    useTreatmentPanelData()
  const { t } = useTranslation()

  const {
    groupedTreatmentItems,
    visibleAppointments,
    visibleSurveys,
    visibleReferrals,
    widthOfArrows,
    treatmentItemViewModel,
    linkingDisabledIds,
    onClickAppointment,
    onClickSurvey,
    editSurvey,
    nextTreatmentItem,
  } = vm

  const renderTreatmentAppointmentItem = (appointment: Appointment) => {
    const treatmentAppointmentVm: TreatmentAppointmentItemComponentViewModel = {
      ...treatmentItemViewModel,
      appointment,
      disableLinking: linkingDisabledIds.includes(appointment.id),
      canLinkTo: !!(
        treatmentItemViewModel.linking &&
        treatmentItemViewModel.linking.id !== appointment.id
      ),
      onClick: onClickAppointment,
    }
    return (
      <TreatmentAppointmentItem
        key={appointment.id}
        vm={treatmentAppointmentVm}
      />
    )
  }

  const renderTreatmentSurveyItem = (survey: Survey) => {
    const treatmentSurveyItemComponentViewModel: TreatmentSurveyItemComponentViewModel =
      {
        firstRef: treatmentItemViewModel.firstRef,
        hasPast: groupedTreatmentItems.hasPast,
        survey,
        onClick: onClickSurvey,
        editSurvey: editSurvey,
      }
    return (
      <TreatmentSurveyItem
        key={survey.id}
        vm={treatmentSurveyItemComponentViewModel}
      />
    )
  }

  const renderTreatmentReferralItem = (referral: Referral) => {
    const vm = {
      referral,
    }
    return <TreatmentReferralItem key={referral.id} vm={vm} />
  }

  const getSortedItems = (groupedItem: TreatmentItems) =>
    [
      ...groupedItem.appointments,
      ...groupedItem.surveys,
      ...groupedItem.referrals,
    ].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime()
    })

  return (
    <>
      {Object.entries(groupedTreatmentItems.groups).map(
        ([groupKey, groupedItem]) => {
          const thisMonth =
            groupedItem.appointments[0]?.date ??
            groupedItem.surveys[0]?.date ??
            groupedItem.referrals[0]?.date

          return (
            <Box key={groupKey} display="flex">
              <Box
                width={TREATMENT_CALENDAR_WIDTH}
                flexShrink={0}
                sx={{ display: { xs: 'none', md: 'block' } }}
              >
                <MonthView
                  month={thisMonth}
                  visibleAppointments={visibleAppointments}
                  visibleSurveys={visibleSurveys}
                  visibleReferrals={visibleReferrals}
                />
              </Box>

              <Box flexGrow={1} mt={{ md: 1.75 }} mr={`${widthOfArrows}px`}>
                {getSortedItems(groupedItem).map((treatmentItem) => {
                  return (
                    <React.Fragment key={treatmentItem.id}>
                      {treatmentItem.id === nextTreatmentItem?.id && (
                        <Box ml={-1.5} mb={2}>
                          <Button
                            variant="text"
                            size="small"
                            disabled={showPastTreatmentItems}
                            onClick={() => setShowPastTreatmentItems(true)}
                          >
                            <ArrowUpIcon sx={{ mr: 1, fontSize: '1.0rem' }} />
                            {t('appointment:range.past')}
                          </Button>
                        </Box>
                      )}
                      {treatmentItem.__typename === 'Appointment' &&
                        renderTreatmentAppointmentItem(treatmentItem)}
                      {treatmentItem.__typename === 'Survey' &&
                        renderTreatmentSurveyItem(treatmentItem)}
                      {treatmentItem.__typename === 'Referral' &&
                        renderTreatmentReferralItem(treatmentItem)}
                    </React.Fragment>
                  )
                })}
              </Box>
            </Box>
          )
        }
      )}
    </>
  )
}
